import React from "react";
import { NavLink, Link } from "react-router-dom";
import { BsWhatsapp } from "react-icons/bs";
import { ExternalLink, ArrowRight, ShieldCheck, Fingerprint } from "lucide-react";
import Logo from "../assets/images/Logo.png";

const serviceLinks = [
  { label: "NIN Verification", href: "/dashboard/verifications/nin" },
  { label: "BVN Verification", href: "/dashboard/verifications/bvn" },
  { label: "IPE Clearance", href: "/dashboard/ipe-clearance" },
  { label: "Check IPE Status", href: "/dashboard/check-ipe-status" },
  { label: "Validation", href: "/dashboard/validation" },
  { label: "Enrollment", href: "/dashboard/enrollment" },
];

const moreServiceLinks = [
  { label: "NIN Modification", href: "/dashboard/modification" },
  { label: "Personalisation", href: "/dashboard/personalisation" },
  { label: "Demographic Search", href: "/dashboard/demographic-search" },
  { label: "BVN Modification", href: "/dashboard/bvn-modification" },
  { label: "BVN Licence", href: "/dashboard/bvn-licence" },
  { label: "CAC Registration", href: "/dashboard/cac" },
  { label: "Banking Agency", href: "/dashboard/bank-agency" },
];

const quickLinks = [
  { label: "Home", to: "/" },
  { label: "Services", href: "/#services" },
  { label: "Sign Up", to: "/signup" },
  { label: "Log In", to: "/login" },
  { label: "Contact Us", to: "/contact" },
];

const Footer = () => {
  return (
    <footer className="bg-[#07001f] text-white relative overflow-hidden">
      {/* Subtle top glow */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-indigo-500/40 to-transparent" />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-indigo-600/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-10">

        {/* Top Row: Brand + Tagline + WhatsApp CTA */}
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-10 pb-12 border-b border-white/10">

          {/* Brand Block */}
          <div className="max-w-xs">
            <Link to="/" className="flex items-center gap-3 mb-4">
              <img src={Logo} alt="Fylder Logo" className="h-10 w-auto object-contain brightness-150" />
              <span className="text-2xl font-extrabold tracking-tight text-white">Fylder</span>
            </Link>
            <p className="text-sm text-slate-400 leading-relaxed">
              A trusted platform delivering secure identity verification—NIN, BVN, and IPE clearance—for businesses and individuals across Nigeria.
            </p>

            {/* WhatsApp CTA */}
            <a
              href="https://wa.me/2349077968959"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2.5 px-5 py-3 bg-green-500/10 hover:bg-green-500/20 border border-green-500/30 text-green-400 rounded-xl text-sm font-bold transition-all group"
            >
              <BsWhatsapp size={16} />
              Chat on WhatsApp
              <ExternalLink size={12} className="opacity-60 group-hover:opacity-100" />
            </a>
          </div>

          {/* Links Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-10 flex-1 md:ml-16">

            {/* Services Column 1 */}
            <div>
              <h3 className="text-[11px] font-extrabold uppercase tracking-[0.15em] text-indigo-400 mb-5">Services</h3>
              <ul className="flex flex-col gap-3">
                {serviceLinks.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-1.5 group"
                    >
                      <span className="w-1 h-1 rounded-full bg-indigo-500/50 group-hover:bg-indigo-400 transition-colors" />
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services Column 2 */}
            <div>
              <h3 className="text-[11px] font-extrabold uppercase tracking-[0.15em] text-indigo-400 mb-5">More Services</h3>
              <ul className="flex flex-col gap-3">
                {moreServiceLinks.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-1.5 group"
                    >
                      <span className="w-1 h-1 rounded-full bg-indigo-500/50 group-hover:bg-indigo-400 transition-colors" />
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-[11px] font-extrabold uppercase tracking-[0.15em] text-indigo-400 mb-5">Quick Links</h3>
              <ul className="flex flex-col gap-3">
                {quickLinks.map((link) =>
                  link.href ? (
                    <li key={link.label}>
                      <a href={link.href} className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-1.5 group">
                        <span className="w-1 h-1 rounded-full bg-indigo-500/50 group-hover:bg-indigo-400 transition-colors" />
                        {link.label}
                      </a>
                    </li>
                  ) : (
                    <li key={link.label}>
                      <Link to={link.to} className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-1.5 group">
                        <span className="w-1 h-1 rounded-full bg-indigo-500/50 group-hover:bg-indigo-400 transition-colors" />
                        {link.label}
                      </Link>
                    </li>
                  )
                )}
              </ul>

              {/* Trust Badges */}
              <div className="mt-8 flex flex-col gap-2">
                <div className="flex items-center gap-2 text-[11px] text-slate-500">
                  <ShieldCheck size={13} className="text-indigo-400" /> NDPR Compliant
                </div>
                <div className="flex items-center gap-2 text-[11px] text-slate-500">
                  <Fingerprint size={13} className="text-indigo-400" /> Biometric Protected
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500">
          <span>© {new Date().getFullYear()} Fylder. All rights reserved.</span>
          <div className="flex items-center gap-5">
            <span className="hover:text-slate-300 cursor-pointer transition-colors">Privacy Policy</span>
            <span className="hover:text-slate-300 cursor-pointer transition-colors">Terms of Service</span>
            <a
              href="https://wa.me/2349077968959"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-green-500/70 hover:text-green-400 transition-colors"
            >
              <BsWhatsapp size={12} /> Support
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
